import type { ErrorResponse } from '$lib/models/types';
import type { ApiErrorResponse, LegacyApiErrorResponse } from '$lib/types';

/**
 * Enhanced error message extraction that supports the new Problem Details format (RFC 7807)
 * @param response - The HTTP response that failed
 * @param fallback - Fallback message if no error information can be extracted
 * @returns Promise<string> - The error message to display to the user
 */
export async function extractErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json() as ApiErrorResponse | LegacyApiErrorResponse | Record<string, unknown>;
    
    // Handle top-level array error responses like [{ key: 'Name', value: 'must be unique' }]
    if (Array.isArray(data) && data.length > 0) {
      try {
        const parts: string[] = [];
        for (const item of data as any[]) {
          if (typeof item === 'string') {
            parts.push(item);
            continue;
          }

          if (item == null) continue;

          if (typeof item === 'object') {
            if ('value' in item && typeof (item as any).value === 'string') {
              parts.push((item as any).value);
              continue;
            }
            if ('message' in item && typeof (item as any).message === 'string') {
              parts.push((item as any).message);
              continue;
            }
            if ('key' in item && 'value' in item && typeof (item as any).key === 'string' && typeof (item as any).value === 'string') {
              parts.push(`${(item as any).key}: ${(item as any).value}`);
              continue;
            }
            // fallback: stringify the object
            parts.push(JSON.stringify(item));
          }
        }

        if (parts.length > 0) return parts.join('; ');
      } catch {
        // ignore extraction errors and continue to other handlers
      }
    }

    // Handle new Problem Details format (RFC 7807)
    if (data && 'detail' in data && typeof data.detail === 'string') {
      return data.detail;
    }
    
    // Handle legacy middleware format (for backward compatibility)
    if (data && 'message' in data && typeof data.message === 'string') {
      return data.message;
    }
    
    // Handle legacy error formats
    if (data && 'error' in data && typeof data.error === 'string') {
      return data.error;
    }
    
    // Handle arrays of errors
    if (data && 'errors' in data && Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0] as string;
    }
    
  } catch {
    // If JSON parsing fails, try to get text content
    try {
      const text = await response.text();
      if (text) {
        return text;
      }
    } catch {
      // Ignore text extraction errors
    }
  }
  
  return fallback;
}

/**
 * Utility to handle API errors consistently across the application
 * @param error - The error object or response
 * @param context - Context information for logging
 * @returns string - User-friendly error message
 */
export function handleApiError(error: Error | Response | Record<string, unknown>, context?: string): string {
  // Log the error for debugging (with context if provided)
  if (context) {
    console.error(`API Error in ${context}:`, error);
  } else {
    console.error('API Error:', error);
  }

  // Extract user-friendly message
  if (error && typeof error === 'object' && 'response' in error) {
    // This is likely an HTTP response error
    const response = error.response;
    if (response && typeof response === 'object') {
      return extractErrorFromResponse(response as Record<string, unknown>);
    }
  }
  
  if (error instanceof Error && error.message) {
    // This is a standard Error object
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Extract error message from an HTTP response (synchronous version)
 * @param response - HTTP response object with parsed data
 * @returns string - Error message
 */
function extractErrorFromResponse(response: Record<string, unknown>): string {
  if (response?.data && typeof response.data === 'object') {
    const data = response.data as Record<string, unknown>;
    
    // Check for Problem Details format first
    if (data.detail && typeof data.detail === 'string') {
      return data.detail;
    }
    
    // Check for legacy format
    if (data.message && typeof data.message === 'string') {
      return data.message;
    }
    
    if (data.error && typeof data.error === 'string') {
      return data.error;
    }
    
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0] as string;
    }
  }
  
  const status = response.status || 'unknown';
  return `Request failed with status ${status}`;
}

/**
 * Format error for user display with optional technical details
 * @param errorMessage - The main error message
 * @param traceId - Optional trace ID for debugging
 * @param showTechnicalDetails - Whether to show technical details in development
 * @returns object with formatted error information
 */
export function formatErrorDisplay(errorMessage: string, traceId?: string, showTechnicalDetails = false) {
  const result = {
    message: errorMessage,
    userMessage: errorMessage,
    traceId,
  };

  // In development mode, we might want to show additional details
  if (showTechnicalDetails && import.meta.env.DEV) {
    return {
      ...result,
      technicalDetails: {
        traceId,
        timestamp: new Date().toISOString(),
      }
    };
  }

  return result;
}

/**
 * Check if an error is a specific type (useful for conditional handling)
 * @param error - The error response
 * @param exceptionType - The exception type name to check for
 * @returns boolean - Whether the error matches the specified type
 */
export function isErrorOfType(error: ApiErrorResponse | LegacyApiErrorResponse | any, exceptionType: string): boolean {
  // For Problem Details format, we can check the type URI or extract exception name
  if (error?.type && typeof error.type === 'string') {
    // Extract exception type from Problem Details URI or direct type field
    return error.type.includes(exceptionType) || error.type === exceptionType;
  }
  return false;
}

/**
 * Determine if an error indicates a client-side issue (4xx) vs server-side (5xx)
 * @param response - HTTP response or status code
 * @returns object indicating error category
 */
export function categorizeError(response: Response | number) {
  const statusCode = typeof response === 'number' ? response : response.status;
  
  return {
    isClientError: statusCode >= 400 && statusCode < 500,
    isServerError: statusCode >= 500,
    isNetworkError: statusCode === 0 || !statusCode,
    statusCode,
    category: statusCode >= 500 ? 'server' : statusCode >= 400 ? 'client' : 'network'
  };
}

/**
 * Parse Problem Details response and extract useful information
 * @param errorData - The parsed error response
 * @returns object with parsed Problem Details information
 */
export function parseProblemDetails(errorData: ApiErrorResponse | LegacyApiErrorResponse | Record<string, unknown>) {
  // Check if this is a Problem Details format response
  if (errorData && typeof errorData === 'object' && 'detail' in errorData) {
    const problemDetails = errorData as ApiErrorResponse;
    return {
      isProblemDetails: true,
      userMessage: problemDetails.detail,
      title: problemDetails.title,
      status: problemDetails.status,
      type: problemDetails.type,
      instance: problemDetails.instance,
      traceId: problemDetails.traceId,
      timestamp: problemDetails.timestamp,
    };
  }
  
  // Check if this is legacy format
  if (errorData && typeof errorData === 'object' && 'message' in errorData) {
    const legacyError = errorData as LegacyApiErrorResponse;
    return {
      isProblemDetails: false,
      userMessage: legacyError.message,
      traceId: legacyError.traceId,
      type: legacyError.type,
    };
  }
  
  return {
    isProblemDetails: false,
    userMessage: 'An unexpected error occurred',
  };
}

/**
 * Extract debugging information from error response
 * @param errorData - The parsed error response
 * @returns object with debugging information
 */
export function extractDebugInfo(errorData: ApiErrorResponse | LegacyApiErrorResponse | Record<string, unknown>) {
  const parsed = parseProblemDetails(errorData);
  
  return {
    traceId: parsed.traceId,
    timestamp: parsed.timestamp,
    type: parsed.type,
    instance: parsed.instance,
    status: parsed.status,
    format: parsed.isProblemDetails ? 'RFC7807' : 'Legacy',
  };
}

// ============================================
// Functions for Component Error Handling
// (Previously in errorResponseUtils.ts)
// ============================================

/**
 * Type guard to check if a response is an error response (either Problem Details or legacy format)
 * @param response - The response to check
 * @returns boolean - True if this is an error response
 */
export function isErrorResponse(response: any): response is ApiErrorResponse | LegacyApiErrorResponse | ErrorResponse {
  if (!response || typeof response !== 'object') {
    return false;
  }
  
  // Check for Problem Details format (RFC 7807)
  if ('detail' in response && typeof response.detail === 'string') {
    return true;
  }
  
  // Check for legacy error format
  if ('message' in response && typeof response.message === 'string') {
    return true;
  }
  
  return false;
}

/**
 * Extract error message from error response (for use in UI components)
 * @param response - Error response object
 * @returns string - User-friendly error message
 */
export function getErrorMessage(response: ApiErrorResponse | LegacyApiErrorResponse | any): string {
  if (!response || typeof response !== 'object') {
    return 'An unexpected error occurred';
  }
  
  // Problem Details format (RFC 7807) - prefer detail over title
  if ('detail' in response && typeof response.detail === 'string') {
    return response.detail;
  }
  
  // Legacy format
  if ('message' in response && typeof response.message === 'string') {
    return response.message;
  }
  
  // Fallback for other error formats
  if ('error' in response && typeof response.error === 'string') {
    return response.error;
  }
  
  return 'An unexpected error occurred';
}

/**
 * Check if error response has debugging information available
 * @param response - Error response object
 * @returns boolean - True if debugging info is available
 */
export function hasDebugInfo(response: ApiErrorResponse | LegacyApiErrorResponse | any): boolean {
  if (!response || typeof response !== 'object') {
    return false;
  }
  
  return !!(response.traceId || response.timestamp || response.instance);
}